import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import FabricTypeahead from './FabricTypeahead';
import { reportError } from '../utils/reportError';

// Deroi · in-house Fabric Cost Estimation (price calculator). All figures per kg.
// Dynamic yarns (1+) + fixed ordered process fields + extra rows + two losses.
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const r2 = (v) => '₹' + (Math.round((num(v)) * 100) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const input = { padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lab = { fontSize: 10, color: '#888', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase' };
const xbtn = { border: 'none', background: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 16, padding: '0 4px' };
const addbtn = { ...input, background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F', cursor: 'pointer', fontWeight: 700 };
// Fixed process fields in exact order (₹/kg).
const FIXED = [
  ['heat_setting', 'Heat Setting'], ['dyeing_or_washing', 'Dyeing or Washing'], ['bio_wash', 'Bio Wash'],
  ['silicon_finish', 'Silicon Finish'], ['value_addition', 'Value Addition'], ['stenter', 'Stenter'],
  ['tubelar', 'Tubelar'], ['compaction', 'Compaction'],
];
const fresh = () => ({
  fabric_name: '', fabric_ref: '', fabric_master_id: '', fabric_master_name: '',
  yarns: [{ description: '', price_per_kg: '', consumption_pct: '', yarn_master_id: '' }],
  knitting_cost: '', knitting_process_loss_pct: '',
  heat_setting: '', dyeing_or_washing: '', bio_wash: '', silicon_finish: '', value_addition: '', stenter: '', tubelar: '', compaction: '',
  added_processes: [], process_loss_pct: '', overheads: '', margin_pct: '',
});

// Mirrors the server's ORDERED formula for instant feedback; server recomputes on save.
function calc(s) {
  const yarns = s.yarns.map(y => ({ d: y.description, price: num(y.price_per_kg), pct: num(y.consumption_pct) }));
  const yarn_cost_net = yarns.reduce((a, y) => a + y.price * (y.pct / 100), 0);
  const pct_sum = yarns.reduce((a, y) => a + y.pct, 0);
  const kploss = num(s.knitting_process_loss_pct), ploss = num(s.process_loss_pct);
  const lines = yarns.filter(y => y.d || y.price || y.pct).map(y => ({ label: 'Yarn — ' + (y.d || 'unnamed'), note: `${y.pct}% × ${r2(y.price)}/kg`, amount: y.price * y.pct / 100 }));
  lines.push({ label: 'Yarn cost (net)', amount: yarn_cost_net, b: 1 });
  lines.push({ label: '+ Knitting', amount: num(s.knitting_cost) });
  let r = yarn_cost_net + num(s.knitting_cost);
  lines.push({ label: '= Yarn + Knitting', amount: r, b: 1 });
  if (kploss !== 0) { const b = r; r = r * (1 + kploss / 100); lines.push({ label: `× Knitting process loss ${kploss}%`, note: 'loss #1', amount: r - b }); }
  lines.push({ label: '= After knitting loss', amount: r, b: 1 });
  for (const [k, label] of FIXED) { const v = num(s[k]); if (v) { r += v; lines.push({ label: '+ ' + label, amount: v }); } }
  for (const p of s.added_processes) { const v = num(p.cost); if (v || p.name) { r += v; lines.push({ label: '+ ' + (p.name || 'Process'), amount: v }); } }
  lines.push({ label: '= After all processes', amount: r, b: 1 });
  if (ploss !== 0) { const b = r; r = r * (1 + ploss / 100); lines.push({ label: `× Process loss ${ploss}%`, note: 'loss #2', amount: r - b }); }
  lines.push({ label: '= After process loss', amount: r, b: 1 });
  r += num(s.overheads); lines.push({ label: '+ Overheads', amount: num(s.overheads) });
  const before = r; lines.push({ label: '= Cost before margin', amount: before, b: 1 });
  const margin = before * num(s.margin_pct) / 100;
  lines.push({ label: `+ Margin (${num(s.margin_pct)}%)`, amount: margin });
  lines.push({ label: '= TOTAL COST / kg', amount: before + margin, b: 1, total: 1 });
  return { pct_sum, pct_warning: Math.abs(pct_sum - 100) > 0.001, total: before + margin, lines };
}

// `prefill` deep-links here from a Deroi Decision fabric line: { line_id, frs_id,
// fabric_name, gsm, composition, label }. The form is seeded from the FRS line and, on
// save, onCreated(estimateId) links the new estimate straight back to that line.
export default function DeroiInHouseFabricCost({ prefill, onCreated, onPrefillConsumed }) {
  const [s, setS] = useState(fresh());
  const [saved, setSaved] = useState([]);
  const [view, setView] = useState(null);
  const [err, setErr] = useState('');
  const [yarnMaster, setYarnMaster] = useState([]);
  const [fabricMaster, setFabricMaster] = useState([]);
  const { user } = useAuth();
  const canEdit = ['admin', 'management'].includes(user?.role);   // edit/approve gated (server also enforces)
  const [editingId, setEditingId] = useState(null);
  const [reason, setReason] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [history, setHistory] = useState(null);   // { estimate_no, rows } | null
  const set = (k, v) => setS(p => ({ ...p, [k]: v }));
  const setYarn = (i, k, v) => setS(p => ({ ...p, yarns: p.yarns.map((y, j) => j === i ? { ...y, [k]: v } : y) }));
  const setProc = (i, k, v) => setS(p => ({ ...p, added_processes: p.added_processes.map((q, j) => j === i ? { ...q, [k]: v } : q) }));

  // Pick a yarn from the shared master → snapshot its rate into the row (still editable).
  const pickYarn = (i, id) => {
    const m = yarnMaster.find(y => String(y.id) === String(id));
    setS(p => ({ ...p, yarns: p.yarns.map((y, j) => j === i ? (m ? { ...y, yarn_master_id: m.id, description: m.yarn_name, price_per_kg: m.standard_rate } : { ...y, yarn_master_id: '' }) : y) }));
  };
  // Optional: pre-seed all yarn rows + %s from a fabric's composition (snapshots rates).
  const preseedFabric = (fabricId) => {
    if (!fabricId) return;
    api.get('/oe/fabric-master/' + fabricId + '/composition').then(r => {
      const rows = (r.data.data || []).map(c => ({ description: c.yarn_name, price_per_kg: c.standard_rate ?? '', consumption_pct: c.percentage ?? '', yarn_master_id: c.yarn_master_id ?? '' }));
      if (rows.length) setS(p => ({ ...p, yarns: rows }));
    }).catch(() => {});
  };

  const load = () => api.get('/deroi/inhouse-estimates').then(r => setSaved(r.data.data || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get('/oe/yarn-master').then(r => setYarnMaster(r.data.data || [])).catch(() => {});
    api.get('/oe/fabric-master').then(r => setFabricMaster(r.data.data || [])).catch(() => {});
  }, []);

  // Seed the blank form from a Deroi Decision fabric line. Only ever seeds a NEW estimate —
  // never clobbers an edit in progress. fabric_ref carries the FRS line's gsm/composition,
  // which have no dedicated field on this form.
  useEffect(() => {
    if (!prefill || editingId) return;
    const ref = [prefill.gsm ? `${prefill.gsm} GSM` : '', prefill.composition || ''].filter(Boolean).join(' · ');
    setS(p => ({ ...fresh(), ...p, fabric_name: prefill.fabric_name || '', fabric_ref: ref.slice(0, 40) }));
    setErr('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [prefill, editingId]);

  const c = calc(s);
  const jarr = (v) => Array.isArray(v) ? v : (typeof v === 'string' ? (() => { try { return JSON.parse(v || '[]'); } catch { return []; } })() : []);
  const rowToForm = (r) => ({
    fabric_name: r.fabric_name || '', fabric_ref: r.fabric_ref || '', estimate_date: r.estimate_date || '',
    fabric_master_id: r.fabric_master_id || '', fabric_master_name: r.fabric_master_name || '',
    yarns: jarr(r.yarns).map(y => ({ description: y.description || '', price_per_kg: y.price_per_kg ?? '', consumption_pct: y.consumption_pct ?? '', yarn_master_id: y.yarn_master_id || '' })),
    knitting_cost: r.knitting_cost ?? '', knitting_process_loss_pct: r.knitting_process_loss_pct ?? '',
    heat_setting: r.heat_setting ?? '', dyeing_or_washing: r.dyeing_or_washing ?? '', bio_wash: r.bio_wash ?? '', silicon_finish: r.silicon_finish ?? '',
    value_addition: r.value_addition ?? '', stenter: r.stenter ?? '', tubelar: r.tubelar ?? '', compaction: r.compaction ?? '',
    added_processes: jarr(r.added_processes).map(p => ({ name: p.name || '', cost: p.cost ?? '' })),
    process_loss_pct: r.process_loss_pct ?? '', overheads: r.overheads ?? '', margin_pct: r.margin_pct ?? '', notes: r.notes || '',
  });
  const cancelEdit = () => { setEditingId(null); setReason(''); setWarnings([]); setS(fresh()); setErr(''); };
  const openEdit = async (id) => {
    setErr('');
    try {
      const g = (await api.get('/deroi/inhouse-estimates/' + id + '/gate-state')).data.data;
      if (!g.gate.editable) {
        const list = (g.gate.unwind || []).map((u, i) => `  ${i + 1}. ${u.label}`).join('\n');
        window.alert((g.gate.hard_stop ? '⛔ ' : '🚫 ') + g.gate.message + (list ? '\n\n' + list : ''));
        return;
      }
      const r = (await api.get('/deroi/inhouse-estimates/' + id)).data.data;
      setS(rowToForm(r)); setEditingId(id); setReason(''); setWarnings(g.gate.warnings || []);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { window.alert(e.response?.data?.error || 'Cannot open edit'); }
  };
  const save = () => {
    setErr('');
    if (!s.fabric_name.trim()) { setErr('Fabric name is required'); return; }
    // Fabric quality required on CREATE (fabric-keyed KNP). On EDIT the API enforces
    // "keep it if the estimate already has one" and does not retro-block legacy estimates,
    // so no client-side edit block is added here.
    if (!editingId && !s.fabric_master_id) { setErr('Fabric master is required — pick the fabric quality this estimate is for.'); return; }
    if (editingId) {
      if (!reason.trim()) { setErr('A revision reason is required to save changes.'); return; }
      api.patch('/deroi/inhouse-estimates/' + editingId, { ...s, reason }).then(() => { cancelEdit(); load(); }).catch(e => setErr(e.response?.data?.error || 'Failed to save'));
    } else {
      api.post('/deroi/inhouse-estimates', s).then(r => {
        setS(fresh()); load();
        // Deep-linked from a fabric line → hand the new estimate back so it can be linked.
        const created = r.data?.data;
        if (prefill && created?.id && onCreated) onCreated(created);
      }).catch(e => setErr(e.response?.data?.error || 'Failed to save'));
    }
  };
  const approve = (id) => api.patch('/deroi/inhouse-estimates/' + id + '/approve').then(load).catch(e => window.alert(e.response?.data?.error || 'Failed'));
  const unapprove = (id) => { const why = window.prompt('Reason to un-approve (logged for audit):', ''); if (why === null) return; if (!why.trim()) return window.alert('A reason is required.'); api.patch('/deroi/inhouse-estimates/' + id + '/unapprove', { reason: why }).then(load).catch(e => window.alert(e.response?.data?.error || 'Failed')); };
  const loadHistory = (e) => api.get('/deroi/inhouse-estimates/' + e.id + '/revisions').then(r => setHistory({ estimate_no: e.estimate_no, rows: r.data.data || [] })).catch(() => {});
  const del = (id) => { if (!window.confirm('Delete this estimate?')) return; api.delete('/deroi/inhouse-estimates/' + id).then(load).catch(reportError('delete this estimate')); };
  // generateKnp removed at Stage 7 step 1 (ruling 10): POST /generate-knp now refuses
  // with KNP_FROZEN, so a button that called it could only produce an error.
  const td = { padding: '7px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5' };
  const numF = (k, label, w = 110) => <div key={k}><label style={lab}>{label}</label><input type="number" step="0.0001" value={s[k]} onChange={e => set(k, e.target.value)} style={{ ...input, width: w }} /></div>;
  const lossF = (k, label) => <div key={k} style={{ background: '#FAEEDA', padding: '4px 8px 6px', borderRadius: 6 }}><label style={{ ...lab, color: '#854F0B' }}>{label}</label><input type="number" step="0.01" value={s[k]} onChange={e => set(k, e.target.value)} style={{ ...input, width: 100 }} /></div>;

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>In-House Fabric Cost Estimation <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>· price calculator, all figures ₹/kg</span></div>

      {prefill && !editingId && (
        <div style={{ marginTop: 10, padding: '9px 12px', background: '#EEEDF8', border: '1px solid #3B3A8F', borderRadius: 6, fontSize: 12, color: '#3B3A8F', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>🔗 Creating an estimate for <b>{prefill.fabric_name || 'this fabric'}</b>{prefill.label ? ` — ${prefill.label}` : ''}. On save it links back to that fabric line automatically.</span>
          <button onClick={() => { setS(fresh()); onPrefillConsumed && onPrefillConsumed(); }}
            style={{ marginLeft: 'auto', padding: '4px 10px', background: 'white', border: '1px solid #3B3A8F', borderRadius: 5, color: '#3B3A8F', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
            Cancel link
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 600px', minWidth: 560 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1 }}><label style={lab}>Fabric name *</label><input value={s.fabric_name} onChange={e => set('fabric_name', e.target.value)} style={{ ...input, width: '100%' }} /></div>
            <div><label style={lab}>Fabric ref</label><input value={s.fabric_ref} onChange={e => set('fabric_ref', e.target.value)} style={{ ...input, width: 130 }} /></div>
          </div>
          {/* Link to the shared fabric master (design A). A KNP generated from this estimate
              keys on this quality; leaving it blank still works but the KNP falls back to
              the KNP-NA numbering and won't consolidate. Request-and-proceed if it's new. */}
          <div style={{ marginBottom: 12 }}>
            <label style={lab}>Fabric master <span style={{ color: '#A32D2D' }}>*</span> <span style={{ color: '#bbb', fontWeight: 500, textTransform: 'none' }}>— required; keys the KNP to this quality</span></label>
            <FabricTypeahead value={s.fabric_master_id} initialLabel={s.fabric_master_name}
              placeholder="Search or create the fabric quality…"
              onChange={(id, obj) => setS(p => ({ ...p, fabric_master_id: id, fabric_master_name: obj ? obj.fabric_name : '' }))} />
          </div>

          {/* YARNS */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>YARNS — consumption % of 1 kg{' '}
              <span style={{ padding: '1px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: c.pct_warning ? '#FCEBEB' : '#EAF7EE', color: c.pct_warning ? '#A32D2D' : '#166534' }}>Σ {c.pct_sum}%{c.pct_warning ? ' ⚠ should be 100' : ' ✓'}</span></span>
            <select defaultValue="" onChange={e => { preseedFabric(e.target.value); e.target.value = ''; }} title="Pre-seed yarn rows + %s from a fabric's composition" style={{ ...input, width: 220, fontSize: 11, marginLeft: 'auto' }}>
              <option value="">↧ Pre-seed yarns from fabric…</option>
              {fabricMaster.map(f => <option key={f.id} value={f.id}>{f.fabric_name}</option>)}
            </select>
          </div>
          {s.yarns.map((y, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <select value={y.yarn_master_id || ''} onChange={e => pickYarn(i, e.target.value)} title="Pick from yarn master (snapshots rate)" style={{ ...input, width: 150 }}>
                <option value="">— pick / custom —</option>
                {yarnMaster.map(m => <option key={m.id} value={m.id}>{m.yarn_name}</option>)}
              </select>
              <input placeholder={`Yarn ${i + 1} description`} value={y.description} onChange={e => setYarn(i, 'description', e.target.value)} style={{ ...input, flex: 1 }} />
              <input type="number" step="0.0001" placeholder="₹/kg" value={y.price_per_kg} onChange={e => setYarn(i, 'price_per_kg', e.target.value)} style={{ ...input, width: 90 }} />
              <input type="number" step="0.01" placeholder="%" value={y.consumption_pct} onChange={e => setYarn(i, 'consumption_pct', e.target.value)} style={{ ...input, width: 70 }} />
              <button onClick={() => setS(p => ({ ...p, yarns: p.yarns.filter((_, j) => j !== i) }))} disabled={s.yarns.length === 1} title="Remove" style={{ ...xbtn, opacity: s.yarns.length === 1 ? 0.3 : 1 }}>✕</button>
            </div>
          ))}
          <button onClick={() => setS(p => ({ ...p, yarns: [...p.yarns, { description: '', price_per_kg: '', consumption_pct: '', yarn_master_id: '' }] }))} style={{ ...addbtn, marginBottom: 16 }}>+ Add Yarn</button>

          {/* FIXED FIELDS in exact order */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {numF('knitting_cost', 'Knitting')}
            {lossF('knitting_process_loss_pct', 'Knitting process loss %')}
            {FIXED.map(([k, label]) => numF(k, label))}
          </div>

          {/* ADD PROCESS rows — inserted before Process Loss */}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#555', margin: '14px 0 6px' }}>Extra processes (optional) — inserted before Process Loss</div>
          {s.added_processes.map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <input placeholder="Process name" value={p.name} onChange={e => setProc(i, 'name', e.target.value)} style={{ ...input, flex: 1 }} />
              <input type="number" step="0.0001" placeholder="₹/kg" value={p.cost} onChange={e => setProc(i, 'cost', e.target.value)} style={{ ...input, width: 100 }} />
              <button onClick={() => setS(p2 => ({ ...p2, added_processes: p2.added_processes.filter((_, j) => j !== i) }))} title="Remove" style={xbtn}>✕</button>
            </div>
          ))}
          <button onClick={() => setS(p => ({ ...p, added_processes: [...p.added_processes, { name: '', cost: '' }] }))} style={{ ...addbtn, marginBottom: 14 }}>+ Add Process</button>

          {/* tail fields */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {lossF('process_loss_pct', 'Process loss %')}
            {numF('overheads', 'Overheads')}
            {numF('margin_pct', 'Margin %')}
          </div>

          {err && <div style={{ color: '#A32D2D', fontSize: 12, marginTop: 10 }}>{err}</div>}
          {editingId && (
            <div style={{ marginTop: 14, padding: '10px 12px', background: '#FFF8E6', border: '1px solid #F5E3B3', borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#8a6d1a', marginBottom: 6 }}>✏️ Editing existing estimate — computed cost/kg recalculates on save; a revision reason is required.</div>
              {warnings.map((w, i) => <div key={i} style={{ fontSize: 11.5, color: '#A32D2D', marginBottom: 4 }}>⚠ {w}</div>)}
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Revision reason (required)…" style={{ ...input, width: '100%', marginTop: 4 }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={save} style={{ ...input, background: '#3B3A8F', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, padding: '8px 18px' }}>{editingId ? '💾 Save changes' : '💾 Save estimate'}</button>
            {editingId && <button onClick={cancelEdit} style={{ ...input, background: '#f0f2f8', border: '1px solid #ddd', cursor: 'pointer', padding: '8px 18px' }}>Cancel</button>}
          </div>
        </div>

        {/* LIVE BUILD-UP */}
        <div style={{ flex: '1 1 320px', minWidth: 300, border: '1px solid #E8E7E7', borderRadius: 10, overflow: 'hidden', position: 'sticky', top: 10 }}>
          <div style={{ background: '#1a1a2e', color: '#fff', padding: '10px 14px', fontWeight: 700, fontSize: 13 }}>Cost build-up (auditable)</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
            {c.lines.map((l, i) => (
              <tr key={i} style={{ background: l.total ? '#EEEDF8' : l.b ? '#f7f7fb' : 'transparent' }}>
                <td style={{ ...td, fontWeight: l.b ? 800 : 400, fontSize: l.total ? 14 : 12 }}>{l.label}{l.note ? <span style={{ color: '#aaa', fontWeight: 400 }}> · {l.note}</span> : ''}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: l.b ? 800 : 500, fontSize: l.total ? 16 : 12, color: l.total ? '#3B3A8F' : '#222', whiteSpace: 'nowrap' }}>{r2(l.amount)}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      </div>

      {/* SAVED */}
      <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', margin: '20px 0 8px' }}>Saved estimates</div>
      <div style={{ border: '1px solid #E8E7E7', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            <th style={{ ...td, fontWeight: 700, textAlign: 'left' }}>Estimate</th><th style={{ ...td, fontWeight: 700, textAlign: 'left' }}>Date</th><th style={{ ...td, fontWeight: 700, textAlign: 'left' }}>Fabric</th><th style={{ ...td, fontWeight: 700 }}>Margin</th><th style={{ ...td, fontWeight: 700, textAlign: 'right' }}>Total ₹/kg</th><th style={{ ...td, fontWeight: 700 }}>KNP</th><th style={td}></th>
          </tr></thead>
          <tbody>
            {saved.length === 0 ? <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#aaa' }}>No saved estimates yet</td></tr>
              : saved.map(e => (
                <tr key={e.id}>
                  <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{e.estimate_no}</td>
                  <td style={td}>{String(e.estimate_date).slice(0, 10)}</td>
                  <td style={td}>{e.fabric_name}{e.fabric_ref ? <span style={{ color: '#999' }}> · {e.fabric_ref}</span> : ''}</td>
                  <td style={td}>{e.margin_pct}%</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{r2(e.total_cost)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {/* Stage 7 step 1 (ruling 10): generating or versioning a KNP is frozen.
                        The existing link still shows — the sheet is still readable. */}
                    {e.knp_no
                      ? <span style={{ padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: '#EEF3FF', color: '#2D2C72' }}>🧶 {e.knp_no} · {e.knp_status}</span>
                      : <span title="frozen — Stage 7 is replacing the KNP with a per-order declaration" style={{ fontSize: 10, color: '#8a8a8a', fontWeight: 600 }}>— frozen</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {e.status === 'approved' && <span style={{ padding: '1px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: '#EAF7EE', color: '#166534', marginRight: 4 }}>✓ approved</span>}
                    <button onClick={() => api.get('/deroi/inhouse-estimates/' + e.id).then(r => setView(r.data.data)).catch(() => {})} style={{ border: 'none', background: 'none', color: '#3B3A8F', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>View</button>
                    {canEdit && <button onClick={() => openEdit(e.id)} title="Edit (recomputes cost; revision logged)" style={{ border: 'none', background: 'none', color: '#3B3A8F', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Edit</button>}
                    {canEdit && (e.status === 'approved'
                      ? <button onClick={() => unapprove(e.id)} style={{ border: 'none', background: 'none', color: '#B45309', cursor: 'pointer', fontSize: 11 }}>Un-approve</button>
                      : <button onClick={() => approve(e.id)} style={{ border: 'none', background: 'none', color: '#166534', cursor: 'pointer', fontSize: 11 }}>Approve</button>)}
                    <button onClick={() => loadHistory(e)} style={{ border: 'none', background: 'none', color: '#888', cursor: 'pointer', fontSize: 11 }}>History</button>
                    <button onClick={() => del(e.id)} style={{ border: 'none', background: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 11 }}>Delete</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {view && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setView(null)}>
          <div style={{ background: '#fff', borderRadius: 12, width: 470, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#2D2C72', color: '#fff', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', borderRadius: '12px 12px 0 0' }}>
              <b>{view.estimate_no} · {view.fabric_name}</b><button onClick={() => setView(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
              {(view.breakdown || []).map((l, i) => (
                <tr key={i} style={{ background: l.total ? '#EEEDF8' : l.bold ? '#f7f7fb' : 'transparent' }}>
                  <td style={{ ...td, fontWeight: l.bold ? 800 : 400 }}>{l.label}{l.note ? <span style={{ color: '#aaa', fontWeight: 400 }}> · {l.note}</span> : ''}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: l.bold ? 800 : 500, color: l.total ? '#3B3A8F' : '#222' }}>{r2(l.amount)}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </div>
      )}

      {history && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setHistory(null)}>
          <div style={{ background: '#fff', borderRadius: 12, width: 620, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#2D2C72', color: '#fff', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', borderRadius: '12px 12px 0 0' }}>
              <b>Revision history · {history.estimate_no}</b><button onClick={() => setHistory(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 16 }}>
              {history.rows.length === 0 && <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: 16 }}>No revisions yet.</div>}
              {history.rows.map(rv => (
                <div key={rv.id} style={{ borderBottom: '1px solid #eee', padding: '8px 0', fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b style={{ color: rv.action === 'unapprove' ? '#B45309' : '#3B3A8F' }}>
                      {rv.action === 'unapprove' ? '🔓 Un-approved' : '✏️ Edited'}{rv.was_approved ? ' (was APPROVED)' : ''}
                    </b>
                    <span style={{ color: '#888' }}>{(rv.revised_at || '').slice(0, 16).replace('T', ' ')} · {rv.revised_by_name || '—'}</span>
                  </div>
                  <div style={{ color: '#555', margin: '2px 0' }}>Reason: {rv.reason}</div>
                  {(rv.changed_fields || []).filter(f => f !== 'status').length > 0 && (
                    <div style={{ fontSize: 11, color: '#666' }}>
                      {(rv.changed_fields || []).filter(f => f !== 'status').map(f => (
                        <span key={f} style={{ marginRight: 10 }}><b>{f}</b>: {String((rv.before || {})[f] ?? '—')} → {String((rv.after || {})[f] ?? '—')}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
